import React, { useState } from 'react';
import { BookOpenIcon, XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const steps = [
  {
    title: 'Adjust Date Range & Retrieve Logs',
    description: 'Choose several days before and after the suspected attacker activity. Click "Retrieve Logs". You can change dates and retrieve logs as many times as needed.',
  },
  {
    title: 'Review Devices',
    description: 'Look at details of devices matching the standard device naming to quickly estimate some of the Safe IPs.',
  },
  {
    title: 'Review IP Addresses',
    description: 'Classify each IP as Safe, Suspect, or Malicious.',
  },
  {
    title: 'Review Enterprise Apps',
    description: 'Flag recognized enterprise apps as "Known" to filter their noise from the timeline.',
  },
  {
    title: 'Enable Filter Toggles',
    description: 'Turn on Safe IPs hidden, Service IPs hidden, and Known Apps hidden to reduce noise.',
  },
  {
    title: 'Review All Events Timeline',
    description: 'Look for the first signs of attacker access.',
  },
  {
    title: 'Review URL Clicks',
    description: 'Check URL clicks prior to the attacker\'s activity. Test suspicious URLs in a sandbox. Classify them when there is strong evidence.',
  },
  {
    title: 'Re-run Retrieve Logs',
    description: 'Re-run if any URL clicks were confirmed as malicious (this searches for other users clicking same URL).',
  },
  {
    title: 'Check Date Range Coverage',
    description: 'Review All Events to determine if the attacker\'s first and last access are fully covered. Change dates and click "Retrieve Logs" if needed.',
  },
  {
    title: 'Dismiss Benign Events',
    description: 'Dismiss remaining visible benign events in the All Events timeline.',
  },
  {
    title: 'Create Story',
    description: 'Click "Client Report" or "Technical Report" in the case header to generate the investigation output in a new tab.',
  },
];

export default function InvestigatorInstructions({ onClose }) {
  const [expandedStep, setExpandedStep] = useState(null);

  return (
    <div className="h-full flex flex-col bg-gray-900 border-r border-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
        <div className="flex items-center gap-2">
          <BookOpenIcon className="h-5 w-5 text-slate-400" />
          <h2 className="text-sm font-bold text-white">Investigation Steps</h2>
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-300 transition-colors"
          title="Close instructions"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Steps list */}
      <div className="flex-1 overflow-y-auto">
        <p className="px-4 pt-3 pb-2 text-xs text-slate-500">
          Follow these steps after creating a new case.
        </p>
        <ol>
          {steps.map((step, idx) => (
            <li
              key={idx}
              className="flex gap-3 px-4 py-3 border-b border-gray-800/50 cursor-pointer hover:bg-gray-800/30 transition-colors"
              onClick={() => setExpandedStep(expandedStep === idx ? null : idx)}
            >
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600/20 text-blue-400 text-xs font-bold flex items-center justify-center mt-0.5">
                {idx + 1}
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-gray-100 flex items-center gap-1">
                  <ChevronRightIcon className={`h-3 w-3 text-gray-500 transition-transform flex-shrink-0 ${expandedStep === idx ? 'rotate-90' : ''}`} />
                  {step.title}
                  {step.comingSoon && (
                    <span className="ml-1.5 text-[10px] font-medium text-amber-400 bg-amber-400/10 px-1.5 py-0.5 rounded-full">
                      Soon
                    </span>
                  )}
                </p>
                {expandedStep === idx && (
                  <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{step.description}</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
